import moment from 'moment'
import { func, object, oneOf, string } from 'prop-types'
import { CalendarProvider, MonthSelector } from '@opallabs/lapidary'
import { kuiperClassNames } from '../../../utils'
import Week from '../Week'
import { WeekHeader } from '../WeekHeader'
import './styles.scss'

const renderWeek = (
  allowable,
  startDay,
  date,
  viewDate,
  onDaySelect,
  classPrefix,
  startOfWeek
) => (
  <Week
    allowable={allowable}
    key={startDay}
    classPrefix={classPrefix}
    date={startDay.clone()}
    selected={date}
    refDate={viewDate}
    startOfWeek={startOfWeek}
    onDaySelect={onDaySelect}
  />
)

const weeks = (
  allowable,
  date,
  viewDate,
  onDaySelect,
  classPrefix,
  startOfWeek
) => {
  // This is a hack. Using `viewDate` directly (or `moment(viewDate)`) will
  // make `startDay` one day after the intended date. This issue is only
  // apparent when the date picker is displaying Monday as the first day of the
  // week. Re-initializing a `moment` object from a `Date` here circumvents the
  // issue, though it's not clear why the start-of-week is being applied
  // inconsistently.
  const viewDateBypassingMomentSOW = moment.isMoment(viewDate)
    ? moment(viewDate.toDate())
    : moment(viewDate)

  const startDay = viewDateBypassingMomentSOW.startOf('month').startOf('week')
  const weeksOut = [
    renderWeek(
      allowable,
      startDay,
      date,
      viewDate,
      onDaySelect,
      classPrefix,
      startOfWeek
    ),
  ]
  while (startDay.add(1, 'week').isSame(viewDate, 'month')) {
    weeksOut.push(
      renderWeek(
        allowable,
        startDay,
        date,
        viewDate,
        onDaySelect,
        classPrefix,
        startOfWeek
      )
    )
  }
  return weeksOut
}

const Month = ({
  allowable = () => {},
  classPrefix = 'date',
  date = moment(),
  onDaySelect = () => {},
  onViewDateChange = () => {},
  theme = 'light',
  viewDate = moment(),
  startOfWeek = 0,
}) => {
  const classes = kuiperClassNames(`${classPrefix}-month`)

  const handleKeyDown = (e) => {
    const newDate = viewDate.clone()
    const originalDate = newDate.toDate()

    if (e.shiftKey) {
      if (e.keyCode === 33) {
        // add year on shift + page up
        newDate.subtract(1, 'years')
      } else if (e.keyCode === 34) {
        // subtract year on shift + page down
        newDate.add(1, 'years')
      }
    } else {
      switch (e.keyCode) {
        case 33: // subtract month on page down
          newDate.subtract(1, 'months')
          break
        case 34: // add month on page up
          newDate.add(1, 'months')
          break
        case 37: // subtract day on left arrow
          newDate.subtract(1, 'days')
          break
        case 38: // subtract week on up arrow
          newDate.subtract(1, 'weeks')
          break
        case 39: // add day on right arrow
          newDate.add(1, 'days')
          break
        case 40: // add week on down arrow
          newDate.add(1, 'weeks')
          break
        default:
          break
      }
    }
    if (newDate.toDate().getTime() !== originalDate.getTime()) {
      onViewDateChange(newDate.toDate())
    }
  }

  return (
    <CalendarProvider locale="en-US">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className={classes} onKeyDown={(e) => handleKeyDown(e)}>
        <MonthSelector
          focusDate={viewDate.toDate()}
          setFocusDate={onViewDateChange}
          theme={theme}
        />

        <table className={kuiperClassNames(`${classPrefix}-month-table`)}>
          <WeekHeader theme={theme} weekStartsOn={startOfWeek} />
          <tbody>
            {weeks(
              allowable,
              date,
              viewDate,
              onDaySelect,
              classPrefix,
              startOfWeek
            )}
          </tbody>
        </table>
      </div>
    </CalendarProvider>
  )
}
Month.displayName = 'Kuiper: unmanaged Month ← Month ← DatePicker ← Inputs'

Month.propTypes = {
  allowable: func,
  classPrefix: string,
  date: object,
  onDaySelect: func,
  onViewDateChange: func,
  startOfWeek: oneOf([0, 1, 2, 3, 4, 5, 6]),
  theme: oneOf(['dark', 'light']).isRequired,
  viewDate: object,
}

export default Month
